import Router from 'next/router';

import handleError from '@pcid/actions/handle-error';
import RequestBuilder from '@pcid/client-request-builder';
import {
	getEmailStorage,
	getRelyingPartyStorage,
	getLoginCtxStorage,
	getCaslStorage,
	getStateStorage,
	getKmsiTokenStorage,
} from '@pcid/storage-utils';
import { generateFingerprint } from '@pcid/utils/fingerprint-utils';
import { trackCreateAccountSuccess, trackCreateAccountFailure, trackCASLEnabled, trackCreateAccountSuccessSnowplow } from '../track-analytics';
import { getEmailFieldError } from '../validate-email';
import { finishLogin, getMiddlewareUrl, storeAuthFactors } from '../login';

const processCreateAccount = ({
	email,
	newPassword,
	passwordStrength: { policyScore },
	mandatory2fa,
	casl,
	...rest
}) => {
	/* put trackCreateAccountSuccessSnowplow here in order to avoid race condition
	and ensure the CreateAccountSuccess event will be triggered & sent to snowplow.
	if the create account call fails, a corresponding tracking event will be sent,
	thus, snowplow team knows this call actually failed */
	trackCreateAccountSuccessSnowplow();

	const { length } = newPassword;
	const deviceDetails = generateFingerprint();
	const rpState = getStateStorage().get();
	const data = {
		email,
		password: newPassword,
		deviceDetails,
		keepMeSignedIn: rpState?.clientId && rpState?.keepMeSignedIn,
		...rest,
	};
	const relyingParty = getRelyingPartyStorage().get();
	const loginCtx = getLoginCtxStorage().getIn(relyingParty);
	if (loginCtx) data.loginCtx = loginCtx;
	return new RequestBuilder(getMiddlewareUrl())
		.withData(data)
		.post('/create-account')
		.then(
			trackCreateAccountSuccess.withMeta({ length, policyScore })
		)
		.then(trackCASLEnabled.withMeta({ casl }))
		.then(({ data: {
			url,
			mfaEnrolmentRequired,
			requestState,
			kmsiToken,
		} = {} }) => {
			getEmailStorage().set(email);
			getCaslStorage().set(casl);
			if (kmsiToken) {
				getKmsiTokenStorage().set(kmsiToken);
			}
			if (requestState && mfaEnrolmentRequired && mandatory2fa) {
				storeAuthFactors({ email, EMAIL: {}, requestState });
				return Router.replace('/login/protect');
			}

			if (url) return finishLogin({ url, lastOp: 'register' });
			return Router.replace('/create-account/success');
		})
		.catch(handleError)
		.catch(trackCreateAccountFailure.withMeta({ length }))
		.catch((error) => {
			const emailFieldError = getEmailFieldError(error);
			if (emailFieldError) {
				const emailError = new Error();
				emailError.fieldErrors = { email: emailFieldError };
				throw emailError;
			}
			throw error;
		});
};

export default processCreateAccount;
